<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  watch,
  getCurrentInstance,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import messageAPI from 'dashboard/api/inbox/message.js'; // GK-HUB: for transcription
import { useMessageContext } from '../provider.js';
const { attachment, showTranscribedText } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();

defineOptions({
  inheritAttrs: false,
});

// GK-HUB: cache-buster estável evita load() que interrompe a reprodução.
// O helper global timeStampAppendedURL usa Date.now() a cada chamada, gerando
// uma URL nova a cada re-avaliação do computed. Como a mensagem é re-serializada
// em todo MESSAGE_UPDATED (status sent/delivered/read, reações, transcrição), a
// URL "mudava" mesmo sem o dataUrl real mudar -> watch -> load() -> áudio parava.
// Fixamos o timestamp uma vez no setup: o computed só muda quando o dataUrl REAL
// muda. Não tocamos o helper global (imagens/vídeo seguem usando-o). O try/catch
// difere do helper (que lança em URL inválida): aqui degradamos graciosamente
// para não quebrar o render com dataUrl vazio durante a janela WebSocket.
const cacheBuster = Date.now(); // não-reativo, uma vez por instância
const timeStampURL = computed(() => {
  const base = attachment.dataUrl;
  if (!base) return '';
  try {
    const url = new URL(base);
    if (!url.searchParams.has('t')) {
      url.searchParams.append('t', cacheBuster);
    }
    return url.toString();
  } catch {
    return base;
  }
});
// GK-HUB: end cache-buster estável

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

// GK-HUB: transcription state
const isTranscribing = ref(false);
const transcribedText = ref(attachment.transcribedText || '');

const { uid } = getCurrentInstance();

// GK-HUB: get message context for API call and detect message type
// GK-HUB: get message context — null outside message list (e.g., search results)
let messageContext = null;
try {
  messageContext = useMessageContext();
} catch {
  // Expected when rendered outside conversation view (search results, previews)
}

// GK-HUB: detect if incoming (client) or outgoing (agent) for styling
const isIncoming = computed(() => {
  return messageContext?.messageType?.value === 0;
});

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration;
};

// GK-HUB: o elemento <audio> não recarrega automaticamente quando o <source>
// muda (comportamento padrão do HTML). Quando a mensagem chega via WebSocket,
// o attachment.dataUrl pode ser atribuído após o mount, e o player ficaria
// travado em 00:00 até o usuário recarregar a página ou trocar de conversa.
// Solução: observar a URL e chamar audioPlayer.load() quando ela mudar.
// Só recarregamos quando a BASE da URL (sem o cache-buster ?t=) realmente muda,
// para nunca interromper a reprodução por ruído de re-serialização (status,
// reações, transcrição). Premissa: dataUrl é um path estável e não-expirável do
// Active Storage (url_for(file), sem assinatura). Se URLs assinadas expiráveis
// forem habilitadas no futuro, comparar a base ainda dispara o load() correto na
// troca real de URL — sem depender da flag isPlaying.
const stripCacheBuster = value => {
  if (!value) return '';
  try {
    const url = new URL(value);
    url.searchParams.delete('t');
    return url.toString();
  } catch {
    return value;
  }
};
watch(timeStampURL, (newUrl, oldUrl) => {
  if (!newUrl || !audioPlayer.value) return;
  if (stripCacheBuster(newUrl) === stripCacheBuster(oldUrl)) return;
  audioPlayer.value.load();
});
// GK-HUB: end watch só recarrega em troca real de URL

// GK-HUB: rede de segurança contra player travado silenciosamente.
// Áudio incoming chega em vários eventos (MESSAGE_CREATED -> anexa mídia -> MESSAGE_UPDATED).
// O <audio class="hidden"> usa <source> filho: se o load() não captura a fonte no instante
// certo (blob ainda propagando ao chegar via WebSocket), o elemento entra em networkState=3
// (NO_SOURCE) SEM emitir evento 'error' e fica travado em 00:00 até um novo load() (ex.: refresh).
// O reload espúrio a cada update (bug do Date.now) mascarava isso; aqui re-tentamos de forma
// cirúrgica: SÓ quando travado (NO_SOURCE) e fora de reprodução — nunca interrompe um áudio OK.
const NETWORK_NO_SOURCE = 3;
const reloadIfStuck = () => {
  const el = audioPlayer.value;
  if (!el || isPlaying.value) return;
  if (el.networkState === NETWORK_NO_SOURCE && timeStampURL.value) {
    el.load();
  }
};
// Cada re-serialização da mensagem (status/transcrição) é uma chance de destravar.
watch(() => attachment, reloadIfStuck);
// GK-HUB: end rede de segurança

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  duration.value = audioPlayer.value?.duration;
  audioPlayer.value.playbackRate = playbackSpeed.value;
  // GK-HUB: força load() no mount. Browsers (especialmente mobile Safari)
  // não preloadam audio em elementos com display:none. Sem isso, o player
  // chegava travado em 00:00 até o usuário recarregar.
  if (audioPlayer.value.readyState === 0) {
    audioPlayer.value.load();
  }
  // GK-HUB: segunda chance — se o load() do mount não pegou a fonte (NO_SOURCE
  // silencioso, típico quando o áudio chega via WebSocket antes do blob propagar),
  // re-tenta uma vez depois de um curto intervalo. reloadIfStuck só age se travado.
  setTimeout(reloadIfStuck, 1500);
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  const el = audioPlayer.value;
  if (isPlaying.value) {
    el.pause();
    isPlaying.value = false;
    return;
  }
  // Emit event to pause all other audio
  emitter.emit('pause_playing_audio', uid);
  isPlaying.value = true;
  // GK-HUB: se já há dados suficientes, toca direto.
  if (el.readyState >= 2) {
    el.play().catch(() => {});
    return;
  }
  // GK-HUB: player ainda sem dados (ou travado em NO_SOURCE quando o áudio chegou via
  // WebSocket) — garante o (re)carregamento e inicia a reprodução assim que estiver pronto.
  const onReady = () => {
    el.removeEventListener('canplay', onReady);
    el.play().catch(() => {});
  };
  el.addEventListener('canplay', onReady);
  if (el.networkState === NETWORK_NO_SOURCE) {
    el.load();
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};

// GK-HUB: manual transcription (async — job processes in background)
const transcribeAudio = async () => {
  if (!messageContext) return;
  const conversationId = messageContext.conversationId?.value;
  const messageId = messageContext.id?.value;
  if (!conversationId || !messageId) return;

  isTranscribing.value = true;
  try {
    const response = await messageAPI.transcribeAudio(
      conversationId,
      messageId
    );
    // 200 = cached result returned immediately
    if (response.data?.transcribed_text) {
      transcribedText.value = response.data.transcribed_text;
      isTranscribing.value = false;
    }
    // 202 = job enqueued, watcher below will pick up the WebSocket update
    // Timeout fallback in case job fails silently
    setTimeout(() => {
      if (isTranscribing.value) isTranscribing.value = false;
    }, 30000);
  } catch {
    isTranscribing.value = false;
  }
};

// GK-HUB: react to WebSocket updates when transcription arrives
watch(
  () => attachment.transcribedText,
  newVal => {
    if (newVal) {
      transcribedText.value = newVal;
      isTranscribing.value = false;
    }
  }
);

const progressPercent = computed(() => {
  return duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0;
});
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="timeStampURL" />
  </audio>
  <!-- GK-HUB: compact audio player - blue for client, dark for agent -->
  <div
    v-bind="$attrs"
    class="rounded-lg w-full gap-1 p-1.5 flex flex-col items-center"
  >
    <div class="flex gap-1.5 w-full items-center">
      <!-- Play/Pause button -->
      <button
        class="p-0 border-0 size-8 rounded-full grid place-content-center flex-shrink-0 transition-colors"
        :class="
          isIncoming
            ? 'bg-n-blue-3 hover:bg-n-blue-4'
            : 'bg-n-alpha-2 hover:bg-n-alpha-3'
        "
        @click="playOrPause"
      >
        <Icon
          v-if="isPlaying"
          class="size-4"
          :class="isIncoming ? 'text-n-blue-9' : ''"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon
          v-else
          class="size-4"
          :class="isIncoming ? 'text-n-blue-9' : ''"
          icon="i-teenyicons-play-small-solid"
        />
      </button>

      <!-- Waveform progress bar -->
      <div class="flex-1 flex flex-col">
        <div
          class="relative w-full h-6 flex items-end gap-[1.5px] cursor-pointer"
          @click="seek"
        >
          <input
            type="range"
            min="0"
            :max="duration"
            :value="currentTime"
            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            @input="seek"
          />
          <!-- Simulated waveform bars -->
          <template v-for="i in 30" :key="i">
            <div
              class="flex-1 rounded-sm transition-all duration-100"
              :class="
                isIncoming
                  ? (i / 30) * 100 <= progressPercent
                    ? 'bg-n-blue-9'
                    : 'bg-n-blue-4'
                  : (i / 30) * 100 <= progressPercent
                    ? 'bg-n-slate-12'
                    : 'bg-n-slate-12/25'
              "
              :style="{
                height: `${8 + Math.sin(i * 0.7 + attachment.id) * 7 + Math.cos(i * 1.3) * 4}px`,
              }"
            />
          </template>
        </div>

        <!-- Time + controls row -->
        <div class="flex items-center justify-between gap-2">
          <div
            class="tabular-nums text-[11px]"
            :class="isIncoming ? 'text-n-slate-12' : 'text-n-slate-11'"
          >
            {{ formatTime(currentTime) }}
            {{ t('AUDIO.TIME_SEPARATOR') }}
            {{ formatTime(duration) }}
          </div>
          <div class="flex items-center gap-1.5">
            <button
              class="border-0 w-8 h-5 grid place-content-center rounded-xl transition-colors"
              :class="
                isIncoming
                  ? 'bg-n-blue-2 hover:bg-n-blue-3'
                  : 'bg-n-alpha-2 hover:bg-n-alpha-3'
              "
              @click="changePlaybackSpeed"
            >
              <span
                class="text-[9px] font-medium"
                :class="isIncoming ? 'text-n-blue-9' : 'text-n-slate-11'"
              >
                {{ playbackSpeedLabel }}
              </span>
            </button>
            <button
              class="p-0 border-0 size-5 grid place-content-center transition-opacity"
              :class="
                isIncoming
                  ? 'text-n-slate-11 hover:text-n-blue-9'
                  : 'opacity-60 hover:opacity-100'
              "
              @click="toggleMute"
            >
              <Icon v-if="isMuted" class="size-3" icon="i-lucide-volume-off" />
              <Icon v-else class="size-3" icon="i-lucide-volume-2" />
            </button>
            <button
              class="p-0 border-0 size-5 grid place-content-center transition-opacity"
              :class="
                isIncoming
                  ? 'text-n-slate-11 hover:text-n-blue-9'
                  : 'opacity-60 hover:opacity-100'
              "
              @click="downloadAudio"
            >
              <Icon class="size-3" icon="i-lucide-download" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- GK-HUB: Transcribed text -->
    <div
      v-if="transcribedText && showTranscribedText"
      class="w-full pt-1"
      :class="
        isIncoming ? 'border-t border-n-blue-4' : 'border-t border-n-container'
      "
    >
      <p class="text-xs leading-normal break-words text-n-slate-11">
        {{ transcribedText }}
      </p>
    </div>

    <!-- GK-HUB: Transcribe button -->
    <div
      v-else-if="showTranscribedText && messageContext"
      class="w-full flex justify-start"
    >
      <button
        v-if="!isTranscribing"
        class="border-0 p-0 flex items-center gap-1 text-[11px] transition-colors cursor-pointer"
        :class="
          isIncoming
            ? 'text-n-blue-9/60 hover:text-n-blue-9'
            : 'text-n-slate-10 hover:text-n-slate-12'
        "
        @click="transcribeAudio"
      >
        <Icon class="size-3" icon="i-lucide-mic" />
        {{ t('AUDIO.TRANSCRIBE_BUTTON') }}
      </button>
      <span
        v-else
        class="flex items-center gap-1.5 text-[11px]"
        :class="isIncoming ? 'text-n-blue-9' : 'text-n-blue-10'"
      >
        <span
          class="size-3 border-2 border-current border-t-transparent rounded-full animate-spin"
        />
        {{ t('AUDIO.TRANSCRIBING') }}
      </span>
    </div>
  </div>
</template>
